import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Stack,
  Breadcrumbs,
  Link,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import jsPDF from "jspdf";
import api from "../api/axios";
import socket from "../socket";
import { CheckCircle, Refresh, NotificationsActive } from "@mui/icons-material";
import ReviewDialog from "../components/reviews/ReviewDialog";

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [openReview, setOpenReview] = useState(false);
  const [reviewCtx, setReviewCtx] = useState(null);
  const [preparationUpdates, setPreparationUpdates] = useState([]);
  const [notification, setNotification] = useState(null);
  const [isSocketReady, setIsSocketReady] = useState(socket.connected);
  const disconnectTimeoutRef = React.useRef(null);

  // 📡 Socket Setup & Real-Time Listeners
  useEffect(() => {
    socket.emit("join_order", orderId);
    console.log("✓ Joined order room:", orderId);

    // Listen for order status & tracking updates
    const handleOrderUpdate = (data) => {
      console.log("📦 Order update received:", data);
      if (data.orderId === orderId) {
        setOrder((prev) => ({
          ...prev,
          status: data.status || prev.status,
          tracking: data.tracking || prev.tracking,
          agent: data.agent || prev.agent,
          agentPhase: data.agentPhase || prev.agentPhase,
          deliveryPhase: data.deliveryPhase || prev.deliveryPhase,
        }));
        setNotification({
          type: "success",
          message: data.message || "Order status updated",
        });
        setTimeout(() => setNotification(null), 3000);
      }
    };

    // Listen for preparation updates (vendor photos, notes)
    const handlePreparationUpdate = (data) => {
      console.log("📸 Preparation update received:", data);
      if (data.orderId === orderId) {
        // Update with full preparation updates array
        setPreparationUpdates(data.preparationUpdates || []);
        // Also update order state with vendorPhase and status
        setOrder((prev) => ({
          ...prev,
          status: data.status || prev.status,
          tracking: data.tracking || prev.tracking,
          vendorPhase: data.vendorPhase || prev.vendorPhase,
        }));
        setNotification({
          type: "info",
          message: data.message || "Vendor shared a preparation update",
        });
        setTimeout(() => setNotification(null), 4000);
      }
    };

    // Handle socket connection
    const handleConnect = () => {
      console.log("✓ Socket connected");
      // Clear any pending disconnect timeout
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }
      setIsSocketReady(true);
      // Only show reconnection message if it was previously disconnected
      setNotification((prev) =>
        prev?.type === "warning" ? {
          type: "success",
          message: "Connection restored",
        } : null
      );
      setTimeout(() => setNotification(null), 2000);
      // Rejoin rooms after reconnection
      socket.emit("join_order", orderId);
    };

    const handleDisconnect = () => {
      console.log("✗ Socket disconnected");
      // Wait 2 seconds before showing disconnect message
      // If connection is restored, don't show it (handled by connect event)
      disconnectTimeoutRef.current = setTimeout(() => {
        console.log("⚠️ Still disconnected after 2s - showing alert");
        setIsSocketReady(false);
        setNotification({
          type: "warning",
          message: "Connection lost - reconnecting...",
        });
      }, 2000);
    };

    const handleConnectError = (error) => {
      console.error("❌ Socket error:", error);
      setNotification({
        type: "error",
        message: "Connection error - updates may be delayed",
      });
    };

    // Register listeners
    socket.on("vendor_order_updated", handleOrderUpdate);
    socket.on("order_status_updated", handleOrderUpdate);
    socket.on("vendor_accepted", handleOrderUpdate);
    socket.on("vendor_preparing", handleOrderUpdate);
    socket.on("vendor_ready", handleOrderUpdate);
    socket.on("vendor_rejected", handleOrderUpdate);
    socket.on("preparation_update", handlePreparationUpdate);
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    return () => {
      // Clean up timeout on unmount
      if (disconnectTimeoutRef.current) {
        clearTimeout(disconnectTimeoutRef.current);
      }
      socket.off("vendor_order_updated", handleOrderUpdate);
      socket.off("order_status_updated", handleOrderUpdate);
      socket.off("vendor_accepted", handleOrderUpdate);
      socket.off("vendor_preparing", handleOrderUpdate);
      socket.off("vendor_ready", handleOrderUpdate);
      socket.off("vendor_rejected", handleOrderUpdate);
      socket.off("preparation_update", handlePreparationUpdate);
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
    };
  }, [orderId]);

  // 📦 Fetch order details on mount
  useEffect(() => {
    api
      .get(`/orders/${orderId}`)
      .then((res) => {
        const orderData = res.data.order;
        setOrder(orderData);
        setPreparationUpdates(orderData.preparationUpdates || []);

        // 👤 Join customer room for personal notifications
        if (orderData.userId) {
          socket.emit("customer_join", orderData.userId);
          console.log("✓ Joined customer room:", orderData.userId);
        }
      })
      .catch((error) => {
        console.error("Failed to load order:", error);
        setOrder(null);
        setNotification({
          type: "error",
          message: "Failed to load order details",
        });
      });
  }, [orderId]);

  const openReviewDialog = async (productId) => {
    const res = await api.get(
      `/reviews/eligibility/${productId}/${order._id}`
    );

    setReviewCtx({
      productId,
      orderId: order._id,
      mode: res.data.mode,
      reviewId: res.data.reviewId,
    });

    setOpenReview(true);
  };

  if (!order) return <Typography>Order not found</Typography>;

  const downloadInvoice = () => {
    const pdf = new jsPDF();
    pdf.text("INVOICE", 20, 20);
    pdf.text(`Order ID: ${order._id}`, 20, 30);
    pdf.text(
      `Date: ${new Date(order.createdAt).toLocaleDateString()}`,
      20,
      40
    );
    pdf.text(`Payment: ${order.paymentMethod}`, 20, 50);
    pdf.save(`Invoice_${order._id}.pdf`);
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", my: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 3, backgroundColor: "#fff", p: 3, borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        <Button
          onClick={() => navigate("/customer/orders")}
          sx={{ mb: 2, textTransform: "none", color: "#007185", fontSize: 14, p: 0 }}
        >
          ← Back to Orders
        </Button>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link
            underline="hover"
            color="inherit"
            href="/customer/dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate("/customer/dashboard");
            }}
            sx={{ display: 'flex', alignItems: 'center', fontSize: 13, color: "#007185" }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Dashboard
          </Link>
          <Typography color="#565959" fontSize={13}>
            Order Details
          </Typography>
        </Breadcrumbs>
      </Box>
      {notification && (
        <Alert
          severity={notification.type}
          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
          onClose={() => setNotification(null)}
          icon={notification.type === "info" ? <NotificationsActive /> : undefined}
        >
          {notification.message}
          {!isSocketReady && notification.type === "warning" && (
            <CircularProgress size={18} sx={{ ml: 1 }} />
          )}
        </Alert>
      )}

      {/* Connection Status Indicator */}
      {!isSocketReady && (
        <Alert severity="warning" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} />
          Reconnecting to real-time updates...
        </Alert>
      )}

      {/* Breadcrumb Navigation */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/customer/dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate("/customer/dashboard");
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Customer Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            href="/customer/orders"
            onClick={(e) => {
              e.preventDefault();
              navigate("/customer/orders");
            }}
          >
            Total Orders
          </Link>
          <Typography color="text.primary">
            Order Details
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Order Summary */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 1, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e7e7e7" }}>
        <Typography variant="h6" fontWeight={700} mb={2} sx={{ color: "#0f1111" }}>
          📦 Order Details
        </Typography>

        <Typography fontSize={14} color="#0f1111">
          <b>Order ID:</b> {order._id}
        </Typography>
        <Typography fontSize={14} color="#0f1111" sx={{ mt: 1 }}>
          <b>Order Date:</b> {new Date(order.createdAt).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {order.items.map((item, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              alignItems: "flex-start",
            }}
          >
            {/* Product Image */}
            <Box
              sx={{
                flexShrink: 0,
                width: 80,
                height: 80,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #e0e0e0",
              }}
            >
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Typography variant="caption" color="textSecondary">
                  No Image
                </Typography>
              )}
            </Box>

            {/* Item Details */}
            <Box>
              <Typography fontWeight={700} color="#0f1111">
                {item.title}
              </Typography>
              <Typography fontSize={13} color="#565959" sx={{ mt: 0.5 }}>
                Qty: {item.qty} · <b>₹{Number(item.price * item.qty).toLocaleString("en-IN")}</b>
              </Typography>

              {order.status === "DELIVERED" && (
                <Button
                  size="small"
                  sx={{
                    textTransform: "none",
                    color: "#007185",
                    fontWeight: 600,
                    mt: 0.5,
                  }}
                  onClick={() =>
                    openReviewDialog(item.productId)
                  }
                >
                  Write / Edit Review
                </Button>
              )}
            </Box>
          </Box>
        ))}

        <Divider sx={{ my: 2, borderColor: "#e7e7e7" }} />

        <Typography fontWeight={700} color="#0f1111">
          Total: <span style={{ color: "#b12704", fontSize: "1.2em" }}>₹{Number(order.total).toLocaleString("en-IN")}</span>
        </Typography>

        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={downloadInvoice}
        >
          Download Invoice
        </Button>
      </Paper>

      {/* Preparation Updates from Vendor */}
      {preparationUpdates && preparationUpdates.length > 0 && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 1, boxShadow: "0 2px 8px rgba(156, 39, 176, 0.15)", border: "1px solid #e0bee7", backgroundColor: "#faf7fc" }}>
          <Typography variant="h6" fontWeight={700} mb={2} sx={{ color: "#6a1b9a" }}>
            💸 Vendor Preparation Updates
          </Typography>
          <Stack spacing={2}>
            {preparationUpdates.map((update, idx) => (
              <Paper key={idx} sx={{ p: 2, backgroundColor: "#ffffff", border: "1px solid #e0bee7", borderRadius: 1 }}>

                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" fontWeight={600}>
                    {update.addedBy || "Vendor"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(update.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                {update.imageUrl && (
                  <Box sx={{ mb: 1, borderRadius: 1, overflow: "hidden", backgroundColor: "#f5f5f5" }}>
                    <img
                      src={update.imageUrl}
                      alt="Preparation"
                      style={{ width: "100%", maxHeight: "200px", objectFit: "cover" }}
                    />
                  </Box>
                )}
                {update.notes && (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {update.notes}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}

      {/* � Agent Assignment Status */}
      {order.agent && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 1, boxShadow: "0 2px 8px rgba(255, 152, 0, 0.15)", border: "1px solid #ffe0b2", backgroundColor: "#fffbf0" }}>
          <Typography variant="h6" fontWeight={700} mb={2} sx={{ color: "#e65100" }}>
            🚲 Delivery Agent Assigned
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Agent Status
              </Typography>
              <Chip 
                label={order.agent.status || "ASSIGNED"} 
                color="warning" 
                sx={{ mt: 1, fontWeight: 600 }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Assigned At
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
                {new Date(order.agent.assignedAt).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Tracking */}
      <Paper sx={{ p: 3, borderRadius: 1, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", border: "1px solid #e7e7e7" }}>
        <Typography fontWeight={700} mb={3} sx={{ color: "#0f1111", fontSize: 16 }}>
          📋 Order Tracking
        </Typography>

        <Stack spacing={1}>
          {order.tracking &&
            order.tracking.map((track, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: 2,
                  backgroundColor: track.completed ? "#f0fdf4" : "#fafafa",
                  borderRadius: 1,
                  borderLeft: track.completed ? "3px solid #10b981" : "3px solid #d1d5db",
                  mb: 1.5,
                }}
              >
                <CheckCircle
                  sx={{
                    fontSize: 22,
                    color: track.completed ? "#10b981" : "#d1d5db",
                    mr: 2,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="#0f1111">
                    {track.label}
                  </Typography>
                  <Typography variant="caption" color="#565959" sx={{ mt: 0.5 }}>
                    {new Date(track.time).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                  </Typography>
                </Box>
                {track.completed && (
                  <Chip label="Completed" color="success" size="small" sx={{ fontWeight: 600, fontSize: 11 }} />
                )}
              </Box>
            ))}
        </Stack>
      </Paper>

      {reviewCtx && (
        <ReviewDialog
          open={openReview}
          onClose={() => setOpenReview(false)}
          productId={reviewCtx.productId}
          orderId={reviewCtx.orderId}
          mode={reviewCtx.mode}
          reviewId={reviewCtx.reviewId}
        />
      )}
    </Box>
  );
};

export default OrderDetails;
